<template>
  <div
    class="order-card"
    :class="{ 'order-card-dark': isDarkMode }"
  >
    <div class="d-flex align-items-center gap-3 mb-3">
      <div
        class="d-flex align-items-center justify-content-center flex-shrink-0 order-logo"
        :class="logoClass"
      >
        <img
          v-if="showShareLogo"
          :src="shareLogoUrl(ISINTrading.ISIN)"
          :alt="ISINTrading.ISINName"
          @error="onLogoError"
        />
        <span v-else>{{ shareMonogram(ISINTrading.ISINName) }}</span>
      </div>
      <div class="min-w-0 flex-grow-1">
        <div class="order-name text-truncate">{{ ISINTrading.ISINName }}</div>
        <div class="order-isin font-monospace">{{ ISINTrading.ISIN }}</div>
      </div>
      <button
        v-if="ISINTrading.ISINID"
        type="button"
        class="forum-bubble-btn"
        :class="{ 'forum-bubble-btn-dark': isDarkMode }"
        :title="'Fórum: ' + ISINTrading.ISINName"
        @click="openShareForum"
      >
        <fa-icon icon="fa-regular fa-comment-dots" />
      </button>
    </div>

    <div class="order-side-row mb-3">
      <div
        v-if="canSwitchSide"
        class="order-side-switch"
        role="group"
        aria-label="Strana pozice"
      >
        <button
          type="button"
          class="order-side-pill is-long"
          :class="{ 'is-active': tradeSide === 'long', 'is-idle': tradeSide !== 'long' }"
          @click="setSide('long')"
        >LONG</button>
        <button
          type="button"
          class="order-side-pill is-short"
          :class="{ 'is-active': tradeSide === 'short', 'is-idle': tradeSide !== 'short' }"
          @click="setSide('short')"
        >SHORT</button>
      </div>
      <span
        v-else
        class="order-side-pill"
        :class="sidePillClass"
      >{{ sideLabel }}</span>
      <div
        v-if="ISINTrading.closePosition === false"
        class="smart-kind-row"
        role="group"
        aria-label="Inteligentní pokyn"
      >
        <button
          type="button"
          class="smart-chip"
          :class="{ 'is-active': protectKind === 'SL', 'is-excluded': protectKind === 'TR' }"
          title="Stop-loss. Nelze kombinovat s trailingem."
          @click="setProtectKind('SL')"
        >Stop-loss</button>
        <button
          type="button"
          class="smart-chip"
          :class="{ 'is-active': tpOn }"
          title="Take-profit. Lze se stop-lossem i trailingem."
          @click="toggleTp"
        >Take-profit</button>
        <button
          type="button"
          class="smart-chip"
          :class="{ 'is-active': protectKind === 'TR', 'is-excluded': protectKind === 'SL' }"
          title="Trailing. Nelze kombinovat se stop-lossem."
          @click="setProtectKind('TR')"
        >Trailing</button>
      </div>
    </div>

    <div class="order-meta mb-3">
      <div class="order-meta-card order-meta-quote">
        <div class="order-meta-cell">
          <div class="order-meta-label">Aktuální cena</div>
          <div class="order-meta-value">{{ formatTrimZeroCents(ISINTrading.ISINPrice) }}</div>
        </div>
        <div v-if="lastPrice != null" class="order-meta-cell">
          <div class="order-meta-label">Předchozí závěr</div>
          <div class="order-meta-value">{{ formatTrimZeroCents(lastPrice) }}</div>
        </div>
        <div v-if="hasPriceChange" class="order-meta-cell">
          <div class="order-meta-label">Změna</div>
          <div class="order-meta-value order-meta-change">
            <span :class="changeClass(priceChange)">{{ formatChangeTypeNumber(priceChange) }}</span>
            <span :class="changeClass(pctChange)">{{ formatChangeTypeNumber(pctChange) }} %</span>
          </div>
        </div>
      </div>
      <div v-if="ISINTrading.Amount" class="order-meta-card order-meta-quote">
        <div class="order-meta-cell">
          <div class="order-meta-label">Aktuální pozice</div>
          <div class="order-meta-value">{{ formatTrimZeroCents(ISINTrading.Amount) }} ks</div>
        </div>
        <div v-if="openPositionSide" class="order-meta-cell">
          <div class="order-meta-label">Typ pozice</div>
          <div class="order-meta-value" :class="openPositionSide === 'SHORT' ? 'is-pos-short' : 'is-pos-long'">
            {{ openPositionSide }}
          </div>
        </div>
        <div v-if="openPositionValue" class="order-meta-cell">
          <div class="order-meta-label">Velikost pozice</div>
          <div class="order-meta-value">{{ openPositionValue }}</div>
        </div>
      </div>
    </div>

    <p v-if="isManageOrders" class="manage-orders-hint mb-3">
      Stop-loss, take-profit nebo trailing k otevřené pozici. SL a trailing nejde kombinovat.
    </p>

    <div
      v-if="ISINTrading.closePosition === false"
      class="order-ways mb-3"
      :class="{ 'is-manage-only': isManageOrders }"
    >
      <div
        v-if="!isManageOrders"
        class="order-ways-top"
        :class="{ 'has-mm': showMoneyManagement }"
      >
        <div class="order-way order-way-max">
          <span class="order-way-label">{{ maxTileLabel }}</span>
          <div class="order-way-row">
            <div class="min-w-0">
              <div class="order-way-value">{{ formatTrimZeroCents(sideRange.max) }} ks</div>
              <div class="order-way-hint">{{ formatPositionValue(sideRange.max) }}</div>
            </div>
            <button
              type="button"
              class="btn btn-primary btn-md mb-0 order-submit-inline-btn"
              :class="submitClass"
              :disabled="isButtonLoading || !sideRange.max"
              @click="investment(null, sideRange.max)"
            >
              {{ actionButtonText }}
            </button>
          </div>
        </div>
        <div v-if="showMoneyManagement" class="order-way order-way-mm">
          <span class="order-way-label">Money management<span v-if="moneyManagementPct"> ({{ moneyManagementPct }}%)</span></span>
          <div class="order-way-row">
            <div class="min-w-0">
              <div class="order-way-value">{{ formatTrimZeroCents(moneyManagementQty) }} ks</div>
              <div class="order-way-hint">{{ formatPositionValue(moneyManagementQty) }}</div>
            </div>
            <button
              type="button"
              class="btn btn-primary btn-md mb-0 order-submit-inline-btn"
              :class="submitClass"
              :disabled="isButtonLoading || !moneyManagementQty"
              @click="investment(null, moneyManagementQty)"
            >
              {{ actionButtonText }}
            </button>
          </div>
        </div>
      </div>
      <div
        v-if="!isManageOrders"
        class="order-way order-way-custom"
      >
        <div class="order-way-head">
          <span class="order-way-label">Zadat počet</span>
          <span v-if="customPositionValue" class="order-way-hint">{{ customPositionValue }}</span>
        </div>
        <div class="order-stepper order-stepper-with-action">
          <button
            v-if="jumpStep"
            type="button"
            class="order-stepper-btn order-stepper-btn-jump"
            :disabled="amountNum <= jumpStep"
            :title="'−' + jumpStep"
            @click="nudge(-jumpStep)"
          >
            <fa-icon icon="fa-solid fa-minus" />
            <span class="jump-num">{{ jumpStep }}</span>
          </button>
          <button
            type="button"
            class="order-stepper-btn"
            :disabled="amountNum <= sideRange.min"
            title="Méně"
            @click="nudge(-1)"
          >
            <fa-icon icon="fa-solid fa-minus" />
          </button>
          <input
            class="order-stepper-input"
            type="text"
            inputmode="numeric"
            pattern="[0-9]*"
            ref="autofocus"
            :placeholder="String(sideRange.min)"
            :value="amount"
            @keydown="onAmountKeydown"
            @paste="onAmountPaste"
            @blur="amountUpdated($event.target.value)"
            @input="event => amountUpdated(event.target.value)"
          />
          <button
            type="button"
            class="order-stepper-btn"
            :disabled="amountNum >= sideRange.max"
            title="Více"
            @click="nudge(1)"
          >
            <fa-icon icon="fa-solid fa-plus" />
          </button>
          <button
            v-if="jumpStep"
            type="button"
            class="order-stepper-btn order-stepper-btn-jump"
            :disabled="amountNum + jumpStep > sideRange.max"
            @click="nudge(jumpStep)"
          >
            <fa-icon icon="fa-solid fa-plus" />
            <span class="jump-num">{{ jumpStep }}</span>
          </button>
          <button
            type="button"
            class="order-stepper-btn order-stepper-btn-preset"
            :disabled="amountNum >= sideRange.max"
            title="Maximum"
            @click="setAmount(sideRange.max)"
          >
            MAX
          </button>
          <button
            v-if="showMoneyManagement"
            type="button"
            class="order-stepper-btn order-stepper-btn-preset"
            :disabled="amountNum === moneyManagementQty"
            title="Money management"
            @click="setAmount(moneyManagementQty)"
          >
            MM
          </button>
          <button
            type="button"
            class="btn btn-primary btn-md mb-0 order-submit-inline-btn"
            :class="submitClass"
            :disabled="isButtonLoading || !amount"
            @click="investment()"
          >
            {{ actionButtonText }}
          </button>
        </div>
      </div>

      <div
        v-if="showSmartPanel"
        class="order-way smart-order"
      >
      <div
        class="smart-split"
        :class="{ 'is-pair': protectKind && tpOn }"
      >
        <div
          v-if="protectKind"
          class="smart-half"
        >
          <div class="order-way-head">
            <span class="order-way-label">{{ protectKind === 'TR' ? 'Trailing' : 'Stop-loss' }}</span>
            <span
              v-if="protectHeadHint"
              class="order-way-hint"
              :class="{ 'is-bad': protectValue && !protectValid }"
              :title="protectHint"
            >{{ protectHeadHint }}</span>
          </div>
          <div class="order-stepper">
            <button
              type="button"
              class="order-stepper-btn order-stepper-btn-preset"
              :class="{ 'is-on': protectUnit === 'pct' }"
              @click="protectUnit = 'pct'"
            >%</button>
            <button
              type="button"
              class="order-stepper-btn order-stepper-btn-preset"
              :class="{ 'is-on': protectUnit === 'czk' }"
              @click="protectUnit = 'czk'"
            >Kč</button>
            <input
              class="order-stepper-input"
              type="text"
              inputmode="decimal"
              :placeholder="smartPlaceholder(protectUnit)"
              :title="protectInputLabel"
              :value="protectValue"
              @input="protectValue = sanitizeSmartInput($event.target.value)"
            >
          </div>
        </div>
        <div
          v-if="tpOn"
          class="smart-half"
        >
          <div class="order-way-head">
            <span class="order-way-label">Take-profit</span>
            <span
              v-if="tpHeadHint"
              class="order-way-hint"
              :class="{ 'is-bad': tpValue && !tpValid }"
              :title="tpHint"
            >{{ tpHeadHint }}</span>
          </div>
          <div class="order-stepper">
            <button
              type="button"
              class="order-stepper-btn order-stepper-btn-preset"
              :class="{ 'is-on': tpUnit === 'pct' }"
              @click="tpUnit = 'pct'"
            >%</button>
            <button
              type="button"
              class="order-stepper-btn order-stepper-btn-preset"
              :class="{ 'is-on': tpUnit === 'czk' }"
              @click="tpUnit = 'czk'"
            >Kč</button>
            <input
              class="order-stepper-input"
              type="text"
              inputmode="decimal"
              :placeholder="smartPlaceholder(tpUnit)"
              :title="tpInputLabel"
              :value="tpValue"
              @input="tpValue = sanitizeSmartInput($event.target.value)"
            >
          </div>
        </div>
      </div>
    </div>

      <button
        v-if="canSaveSmartOnly"
        type="button"
        class="btn btn-md mb-0 smart-save-btn"
        :class="isDarkMode ? 'smart-save-btn-dark' : ''"
        :disabled="isButtonLoading || !smartSaveReady"
        @click="saveSmartOrders"
      >
        {{ smartSaveLabel }}
      </button>
    </div>

    <argon-button
      v-if="ISINTrading.closePosition !== false"
      @click="investment('closePosition')"
      class="text-md w-100 mt-3"
      color="primary"
      size="md"
      :disabled="isButtonLoading">
      Uzavření pozice
    </argon-button>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, inject, onMounted } from 'vue';
import { useStore } from 'vuex';
import dataService from '@/services/dataService';
import ArgonButton from "@/components/ArgonButton.vue";
import { shareLogoUrl, shareMonogram } from '@/helpers/shareLogos';
import { formatTrimZeroCents } from '@/utils/NumberFormat';

const props = defineProps({
  gameID: Number,
  ISINTrading: Object,
  availableCapital: {
    type: Number,
    default: null
  },
  totalCapital: {
    type: Number,
    default: null
  }
});
const emit = defineEmits(['reload-data']);

const store = useStore();
const swal = inject('$swal');
const isDarkMode = computed(() => !!store.state.isDarkMode);

const amount = ref(null);
const autofocus = ref(null);
const logoFailed = ref(false);
const mmPercent = ref(null);
const protectKind = ref(null);
const protectUnit = ref('pct');
const protectValue = ref('');
const tpOn = ref(false);
const tpUnit = ref('pct');
const tpValue = ref('');

const userID = computed(() => store.getters.getUser.UserID);
const sessionHash = computed(() => store.getters.isLoggedIn);
const isButtonLoading = computed(() => store.getters['loadingModule/isButtonLoading']);

const showShareLogo = computed(() => !!(
  shareLogoUrl(props.ISINTrading?.ISIN) && !logoFailed.value
));
const logoClass = computed(() => showShareLogo.value ? '' : 'order-logo-fallback');

const openPositionQty = computed(() => toNum(props.ISINTrading?.Amount) || 0);

const isManageOrders = computed(() => !!props.ISINTrading?.manageOrders);

const canSwitchSide = computed(() => (
  props.ISINTrading.closePosition === false
  && !isManageOrders.value
  && openPositionQty.value === 0
));

const tradeSide = computed({
  get: () => {
    if (props.ISINTrading?.side) return props.ISINTrading.side;
    if (openPositionQty.value < 0) return 'short';
    if (openPositionQty.value > 0) return 'long';
    return 'long';
  },
  set: (value) => {
    if (props.ISINTrading) props.ISINTrading.side = value;
  },
});

const sideRange = computed(() => {
  const min = parseInt(props.ISINTrading.AmountMin, 10) || 0;
  const max = parseInt(props.ISINTrading.AmountMax, 10) || 0;
  if (tradeSide.value === 'long') {
    return { min: 1, max: Math.max(1, max) };
  }
  if (tradeSide.value === 'short') {
    return { min: 1, max: Math.max(1, Math.abs(Math.min(0, min))) };
  }
  return { min, max };
});

const sideLabel = computed(() => {
  if (props.ISINTrading.closePosition) return 'Uzavření';
  if (tradeSide.value === 'short') return 'SHORT';
  if (tradeSide.value === 'long') return 'LONG';
  return 'Pokyn';
});

const sidePillClass = computed(() => {
  if (props.ISINTrading.closePosition) return 'is-close';
  if (tradeSide.value === 'short') return 'is-short';
  return 'is-long';
});

const submitClass = computed(() => {
  if (tradeSide.value === 'short') return 'order-submit-short';
  if (tradeSide.value === 'long') return 'order-submit-long';
  return '';
});

const amountNum = computed(() => {
  const n = parseInt(amount.value, 10);
  return Number.isFinite(n) ? n : 0;
});

const jumpStep = computed(() => {
  const max = sideRange.value.max;
  if (max > 1000) return 100;
  if (max > 100) return 10;
  return 0;
});

const maxTileLabel = computed(() => {
  if (tradeSide.value === 'short') return 'Prodat maximum';
  return 'Koupit maximum';
});

const actionButtonText = computed(() => {
  if (tradeSide.value === 'short') return 'Prodat';
  if (tradeSide.value === 'long') return 'Koupit';
  return 'Zadat pokyn';
});

const sharePrice = computed(() => (
  toNum(props.ISINTrading?.ISINPrice)
  || toNum(props.ISINTrading?.ISINLastPrice)
  || toNum(props.ISINTrading?.LPrc)
  || toNum(props.ISINTrading?.Prc)
  || 0
));

const formatPositionValue = (qty) => {
  const n = Number(qty);
  const price = sharePrice.value;
  if (!Number.isFinite(n) || n <= 0 || !price) return '';
  return `${formatTrimZeroCents(n * price)} Kč`;
};

const customPositionValue = computed(() => formatPositionValue(amountNum.value));

const openPositionSide = computed(() => {
  if (openPositionQty.value < 0) return 'SHORT';
  if (openPositionQty.value > 0) return 'LONG';
  return null;
});

const openPositionValue = computed(() => formatPositionValue(Math.abs(openPositionQty.value)));

const moneyManagementPct = computed(() => mmPercent.value);

const moneyManagementQty = computed(() => {
  const price = sharePrice.value;
  const total = props.totalCapital;
  const pct = mmPercent.value;
  if (!price || price <= 0 || total == null || total <= 0 || pct == null || pct <= 0) return 0;
  const byMm = Math.floor((total * (pct / 100)) / price);
  return byMm >= 1 ? byMm : 0;
});

const showMoneyManagement = computed(() => {
  const qty = moneyManagementQty.value;
  const price = sharePrice.value;
  const cash = props.availableCapital;
  if (qty < 1 || !price || cash == null || cash <= 0) return false;
  return Math.floor(cash / price) >= qty;
});

const toNum = (value) => {
  if (value == null || value === '') return null;
  const n = Number(String(value).replace(/\s/g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

const priceChange = computed(() => toNum(
  props.ISINTrading?.PrcDf ?? props.ISINTrading?.ISINPriceListAmtDiff
));
const pctChange = computed(() => toNum(
  props.ISINTrading?.PctDf ?? props.ISINTrading?.ISINPriceListPctDiff
));
const lastPrice = computed(() => toNum(
  props.ISINTrading?.ISINLastPrice ?? props.ISINTrading?.LPrc
));
const hasPriceChange = computed(() => priceChange.value != null || pctChange.value != null);

const formatChangeTypeNumber = (value) => store.getters.formatChangeTypeNumber(value);
const changeClass = (value) => {
  const n = toNum(value);
  if (n == null || n === 0) return 'is-flat';
  return n > 0 ? 'is-up' : 'is-down';
};

const parseQty = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) return Math.trunc(value);
  const digits = String(value ?? '').replace(/\D/g, '');
  if (!digits) return NaN;
  return parseInt(digits, 10);
};

const clampAmount = (value) => {
  const n = parseQty(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  const min = sideRange.value.min || 1;
  const max = sideRange.value.max;
  if (!Number.isFinite(max) || max < min) return null;
  return Math.min(max, Math.max(min, n));
};

const syncAmountInput = () => {
  nextTick(() => {
    if (autofocus.value) {
      autofocus.value.value = amount.value == null ? '' : String(amount.value);
    }
  });
};

const nudge = (delta) => {
  const current = amountNum.value || 0;
  const raw = current + delta;
  if (raw <= 0) {
    amount.value = String(sideRange.value.min || 1);
    syncAmountInput();
    return;
  }
  const next = clampAmount(raw);
  amount.value = next == null ? null : String(next);
  syncAmountInput();
};

const setAmount = (qty) => {
  const next = clampAmount(qty);
  amount.value = next == null ? null : String(next);
  syncAmountInput();
};

const setSide = (side) => {
  if (!canSwitchSide.value || (side !== 'long' && side !== 'short')) return;
  if (tradeSide.value === side) return;
  tradeSide.value = side;
  store.dispatch('openModal', {
    title: side === 'short' ? 'Otevřít SHORT' : 'Otevřít LONG',
    view: 'Investment',
  });
  if (amount.value) setAmount(amount.value);
};

const roundMoney = (n) => Math.round(n * 100) / 100;

const parseSmartNum = (raw) => {
  const n = Number(String(raw ?? '').replace(/\s/g, '').replace(',', '.'));
  return Number.isFinite(n) && n > 0 ? n : null;
};

const sanitizeSmartInput = (raw) => String(raw ?? '').replace(/[^\d.,]/g, '');

const showSmartPanel = computed(() => !!protectKind.value || tpOn.value);

const setProtectKind = (kind) => {
  if (protectKind.value === kind) {
    protectKind.value = null;
    protectValue.value = '';
    return;
  }
  protectKind.value = kind;
  protectValue.value = '';
};

const toggleTp = () => {
  tpOn.value = !tpOn.value;
  if (!tpOn.value) tpValue.value = '';
};

const isSmartLong = computed(() => tradeSide.value !== 'short');

const smartPlaceholder = (unit) => (unit === 'pct' ? 'např. 5' : 'např. 950');

const limitForKind = (kind, unit, raw) => {
  const price = sharePrice.value;
  const n = parseSmartNum(raw);
  if (!price || n == null) return null;
  const long = isSmartLong.value;
  if (kind === 'TR') {
    if (unit === 'pct') {
      if (n >= 100) return null;
      return roundMoney(long ? price * (1 - n / 100) : price * (1 + n / 100));
    }
    return roundMoney(long ? price - n : price + n);
  }
  if (unit === 'czk') return roundMoney(n);
  if (n >= 100) return null;
  if (kind === 'SL') {
    return roundMoney(long ? price * (1 - n / 100) : price * (1 + n / 100));
  }
  return roundMoney(long ? price * (1 + n / 100) : price * (1 - n / 100));
};

const validForKind = (kind, unit, raw) => {
  const price = sharePrice.value;
  const limit = limitForKind(kind, unit, raw);
  const n = parseSmartNum(raw);
  if (!price || n == null || limit == null || limit <= 0) return false;
  const long = isSmartLong.value;
  if (kind === 'TR') return true;
  if (kind === 'SL') return long ? limit < price : limit > price;
  return long ? limit > price : limit < price;
};

const payloadForKind = (kind, unit, raw) => {
  const n = parseSmartNum(raw);
  const limit = limitForKind(kind, unit, raw);
  if (!validForKind(kind, unit, raw) || n == null) return null;
  let type = 'POT_TP';
  if (kind === 'SL') type = 'POT_SL';
  else if (kind === 'TR') type = unit === 'pct' ? 'POT_TRP' : 'POT_TRC';
  return {
    PositionOrderType: type,
    PositionOrderLimitPrice: limit || 0,
    PositionOrderTrailing: (type === 'POT_TRP' || type === 'POT_TRC') ? n : null,
    PositionOrderPercent: unit === 'pct' ? n : 0,
  };
};

const protectInputLabel = computed(() => {
  if (protectKind.value !== 'TR' && protectUnit.value === 'czk') return 'Limitní cena';
  return 'Odchylka';
});
const tpInputLabel = computed(() => (tpUnit.value === 'czk' ? 'Limitní cena' : 'Odchylka'));

const protectLimitPrice = computed(() => (
  protectKind.value ? limitForKind(protectKind.value, protectUnit.value, protectValue.value) : null
));
const tpLimitPrice = computed(() => (
  tpOn.value ? limitForKind('TP', tpUnit.value, tpValue.value) : null
));
const protectValid = computed(() => (
  !!protectKind.value && validForKind(protectKind.value, protectUnit.value, protectValue.value)
));
const tpValid = computed(() => tpOn.value && validForKind('TP', tpUnit.value, tpValue.value));

const formatLimitHint = (limit, valid, raw) => {
  if (valid && limit != null) return `${formatTrimZeroCents(limit)} Kč`;
  if (raw && !valid) return 'NELZE';
  return '';
};
const protectHeadHint = computed(() => (
  protectKind.value
    ? formatLimitHint(protectLimitPrice.value, protectValid.value, protectValue.value)
    : ''
));
const tpHeadHint = computed(() => (
  tpOn.value
    ? formatLimitHint(tpLimitPrice.value, tpValid.value, tpValue.value)
    : ''
));

const protectHint = computed(() => {
  const price = sharePrice.value;
  if (!price) return 'Bez aktuální ceny nejde pokyn spočítat.';
  if (!protectKind.value) return '';
  const n = parseSmartNum(protectValue.value);
  if (n == null) {
    if (protectKind.value === 'TR') {
      return protectUnit.value === 'pct'
        ? 'Odchylka v % od plovoucího maxima (long) nebo minima (short).'
        : 'Odchylka v Kč od plovoucího maxima (long) nebo minima (short).';
    }
    return isSmartLong.value
      ? 'Stop pod trhem. % se přepočte na cenu v Kč.'
      : 'Stop nad trhem. % se přepočte na cenu v Kč.';
  }
  const limit = protectLimitPrice.value;
  if (!protectValid.value || limit == null) return 'Tahle čára k této straně pozice nesedí.';
  const px = `${formatTrimZeroCents(limit)} Kč`;
  if (protectKind.value === 'TR') {
    return `Teď by spoušť byla ${px}. S růstem (long) nebo poklesem (short) se posune.`;
  }
  return `Prodej / vykrytí při ${px}.`;
});

const tpHint = computed(() => {
  const price = sharePrice.value;
  if (!price) return 'Bez aktuální ceny nejde pokyn spočítat.';
  const n = parseSmartNum(tpValue.value);
  if (n == null) {
    return isSmartLong.value
      ? 'Cíl nad trhem. % se přepočte na cenu v Kč.'
      : 'Cíl pod trhem. % se přepočte na cenu v Kč.';
  }
  const limit = tpLimitPrice.value;
  if (!tpValid.value || limit == null) return 'Tahle čára k této straně pozice nesedí.';
  return `Zisk na ${formatTrimZeroCents(limit)} Kč.`;
});

const smartOrderPayloads = computed(() => {
  const list = [];
  if (protectKind.value) {
    const protect = payloadForKind(protectKind.value, protectUnit.value, protectValue.value);
    if (protect) list.push(protect);
  }
  if (tpOn.value) {
    const tp = payloadForKind('TP', tpUnit.value, tpValue.value);
    if (tp) list.push(tp);
  }
  return list;
});

const canSaveSmartOnly = computed(() => (
  props.ISINTrading.closePosition === false && openPositionQty.value !== 0
));

const existingOrderTypes = computed(() => {
  const t = props.ISINTrading || {};
  const types = [];
  if (toNum(t.PO_SL) != null) types.push('POT_SL');
  if (toNum(t.PO_TRP) != null) types.push('POT_TRP');
  if (toNum(t.PO_TRC) != null) types.push('POT_TRC');
  if (toNum(t.PO_TP) != null) types.push('POT_TP');
  if (toNum(t.PO_TR) != null && !types.includes('POT_TRP') && !types.includes('POT_TRC')) {
    types.push('POT_TRP');
  }
  return types;
});

const smartSaveReady = computed(() => {
  if (protectKind.value && !protectValid.value) return false;
  if (tpOn.value && !tpValid.value) return false;
  if (smartOrderPayloads.value.length) return true;
  return existingOrderTypes.value.length > 0;
});

const smartSaveLabel = computed(() => (
  smartOrderPayloads.value.length ? 'Uložit inteligentní pokyn' : 'Smazat inteligentní pokyn'
));

const hydrateExistingOrders = () => {
  const t = props.ISINTrading || {};
  const sl = toNum(t.PO_SL);
  const tr = toNum(t.PO_TR);
  const tp = toNum(t.PO_TP);
  const trp = toNum(t.PO_TRP);
  const trc = toNum(t.PO_TRC);
  if (sl != null) {
    protectKind.value = 'SL';
    protectUnit.value = 'czk';
    protectValue.value = String(sl);
  } else if (tr != null) {
    protectKind.value = 'TR';
    if (trp != null) {
      protectUnit.value = 'pct';
      protectValue.value = String(trp);
    } else {
      protectUnit.value = 'czk';
      protectValue.value = trc != null ? String(trc) : '';
    }
  }
  if (tp != null) {
    tpOn.value = true;
    tpUnit.value = 'czk';
    tpValue.value = String(tp);
  }
};

const saveSmartOrders = async () => {
  if (!canSaveSmartOnly.value || !smartSaveReady.value) return;
  const wanted = smartOrderPayloads.value;
  const wantedTypes = wanted.map((item) => item.PositionOrderType);
  const toDelete = existingOrderTypes.value.filter((type) => !wantedTypes.includes(type));
  try {
    store.dispatch('loadingModule/setButtonLoading', true);
    const results = [];
    for (const type of toDelete) {
      results.push(await dataService.ClearPositionOrder(
        userID.value,
        sessionHash.value,
        props.gameID,
        props.ISINTrading.ISINID,
        type
      ));
    }
    if (!wanted.length && !toDelete.length && existingOrderTypes.value.length) {
      results.push(await dataService.ClearPositionOrder(
        userID.value,
        sessionHash.value,
        props.gameID,
        props.ISINTrading.ISINID,
        ''
      ));
    }
    for (const payload of wanted) {
      results.push(await dataService.SetPositionOrder(
        userID.value,
        sessionHash.value,
        props.gameID,
        props.ISINTrading.ISINID,
        payload
      ));
    }
    const ok = results.length > 0 && results.every((item) => item?.Result);
    let messages = [];
    if (ok) {
      if (wanted.length > 1) {
        messages = ['Inteligentní pokyny byly uloženy'];
      } else if (wanted.length === 1) {
        messages = ['Inteligentní pokyn byl uložen'];
      } else {
        messages = [toDelete.length > 1 ? 'Inteligentní pokyny byly smazány' : 'Inteligentní pokyn byl smazán'];
      }
    } else {
      results.forEach((item) => {
        if (!item?.Result) {
          messages.push(...(item?.ErrorMessage || ['Inteligentní pokyn se nepodařilo uložit']));
        }
      });
      if (!messages.length) messages.push('Inteligentní pokyn se nepodařilo uložit');
    }
    if (ok) emit('reload-data');
    store.dispatch('closeModal');
    swal.fire({
      position: 'center',
      icon: ok ? 'success' : 'error',
      html: messages.map((message) => `<h5>${message}</h5>`).join(''),
      showConfirmButton: false,
      timer: 3000
    });
  } catch (error) {
    console.error(error);
  } finally {
    store.dispatch('loadingModule/setButtonLoading', false);
  }
};

const amountUpdated = (raw) => {
  const digits = String(raw ?? '').replace(/\D/g, '');
  if (!digits) {
    amount.value = null;
    syncAmountInput();
    return;
  }
  const next = clampAmount(digits);
  amount.value = next == null ? null : String(next);
  syncAmountInput();
};

const onAmountKeydown = (event) => {
  if (['-', '+', 'e', 'E', '.', ',', ' '].includes(event.key)) {
    event.preventDefault();
  }
};

const onAmountPaste = (event) => {
  event.preventDefault();
  const text = event.clipboardData?.getData('text') || '';
  amountUpdated(text);
};

const onLogoError = () => {
  logoFailed.value = true;
};

const openShareForum = () => {
  if (!props.ISINTrading?.ISINID) return;
  store.dispatch('openForum', {
    title: props.ISINTrading.ISINName || 'Akcie',
    ISINID: props.ISINTrading.ISINID,
    ISIN: props.ISINTrading.ISIN
  });
};

const investment = async (action, qtyOverride) => {
  const orderPayloads = action === 'closePosition' ? [] : smartOrderPayloads.value;
  const firstOrder = orderPayloads[0] || null;
  try {
    store.dispatch('loadingModule/setButtonLoading', true);
    const entered = qtyOverride != null
      ? clampAmount(qtyOverride)
      : clampAmount(amount.value);
    if (action !== 'closePosition' && !entered) return;
    const signed = tradeSide.value === 'short'
      ? -Math.abs(entered)
      : (tradeSide.value === 'long' ? Math.abs(entered) : entered);
    const amountTotal = action === 'closePosition' ? parseInt(props.ISINTrading.Amount) * -1 : signed;
    const response = await dataService.Investment(
      userID.value,
      sessionHash.value,
      amountTotal,
      props.gameID,
      props.ISINTrading.ISINID,
      firstOrder
    );

    const orderSavedInTrade = !!(response?.Data?.PositionOrder)
      || (response?.InfoMessage || []).some((msg) => String(msg).includes('Inteligentní pokyn'));
    const extraOrders = firstOrder && orderSavedInTrade
      ? orderPayloads.slice(1)
      : orderPayloads;
    const extraResults = [];
    if (response?.Result) {
      for (const payload of extraOrders) {
        extraResults.push(await dataService.SetPositionOrder(
          userID.value,
          sessionHash.value,
          props.gameID,
          props.ISINTrading.ISINID,
          payload
        ));
      }
    }

    if (response?.Result) emit('reload-data');
    store.dispatch('closeModal');

    const ok = !!response?.Result;
    const extraFailed = extraResults.some((item) => !item?.Result);
    const messages = [];
    if (ok) {
      const tradeMsgs = (response.InfoMessage || ['Pokyn ve hře byl zadán'])
        .filter((msg) => !String(msg).includes('Inteligentní pokyn'));
      messages.push(...(tradeMsgs.length ? tradeMsgs : ['Pokyn ve hře byl zadán']));
      if (orderPayloads.length > 1 && !extraFailed) {
        messages.push('Inteligentní pokyny byly uloženy');
      } else if (orderPayloads.length === 1 && !extraFailed) {
        messages.push('Inteligentní pokyn byl uložen');
      } else if (extraFailed) {
        extraResults.forEach((item) => {
          if (!item?.Result) {
            messages.push(...(item?.ErrorMessage || ['Obchod prošel, inteligentní pokyn se neuložil']));
          }
        });
      }
    } else {
      messages.push(...(response?.ErrorMessage || ['Pokyn se nepodařilo odeslat']));
    }

    swal.fire({
      position: 'center',
      icon: ok ? (extraFailed ? 'warning' : 'success') : 'error',
      html: messages.map((message) => `<h5>${message}</h5>`).join(''),
      showConfirmButton: false,
      timer: 3000
    });
  } catch (error) {
    console.error(error);
  } finally {
    store.dispatch('loadingModule/setButtonLoading', false);
  }
};

const parsePercent = (value) => {
  const n = Number(String(value ?? '').replace(',', '.').replace(/[^\d.]/g, ''));
  return Number.isFinite(n) && n > 0 ? n : null;
};

const loadMoneyManagement = async () => {
  if (!userID.value || !sessionHash.value) return;
  try {
    const response = await dataService.GetUserSetting(userID.value, sessionHash.value);
    const list = Array.isArray(response?.Data) ? response.Data : [];
    const item = list.find((row) => String(row?.UserSettingType || '').toLowerCase() === 'money management');
    if (!item) return;
    const values = item.Values || [];
    const selected = values.find(
      (option) => String(option.UserSettingTypeValueID) === String(item.UserSettingValue)
    ) || values.find(
      (option) => String(option.UserSettingTypeValueID) === String(item.UserSettingTypeDflt)
    );
    mmPercent.value = parsePercent(selected?.UserSettingTypeValue);
  } catch (error) {
    console.error(error);
  }
};

onMounted(() => {
  if (!isManageOrders.value) loadMoneyManagement();
  hydrateExistingOrders();
  if (props.ISINTrading.closePosition === false && !isManageOrders.value) {
    nextTick(() => {
      autofocus.value?.focus();
    });
  }
});
</script>

<style scoped>
.order-card {
  padding: 0.35rem 0.85rem 0.5rem;
}

.order-logo {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
}

.order-logo img {
  width: 40px;
  height: 40px;
  object-fit: contain;
}

.order-logo-fallback {
  background: rgba(100, 116, 139, 0.16);
  color: #64748b;
  font-size: 0.72rem;
  font-weight: 700;
}

.order-name {
  font-weight: 800;
  font-size: 1.05rem;
  color: #0F172A;
  line-height: 1.2;
}

.order-isin {
  font-size: 0.75rem;
  color: #64748B;
  margin-top: 0.15rem;
}

.order-card-dark .order-name {
  color: #E2E8F0;
}

.order-card-dark .order-isin {
  color: #94A3B8;
}

.forum-bubble-btn {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 1.5px solid #CBD5E1;
  background: transparent;
  color: #64748B;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  flex-shrink: 0;
  font-size: 0.78rem;
}

.forum-bubble-btn:hover {
  color: #FF8A4C;
  border-color: #FF8A4C;
  background: rgba(255, 138, 76, 0.08);
}

.forum-bubble-btn-dark {
  border-color: #475569;
  color: #94A3B8;
}

.forum-bubble-btn-dark:hover {
  color: #FF8A4C;
  border-color: #FF8A4C;
  background: rgba(255, 138, 76, 0.14);
}

.order-side-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.45rem 0.75rem;
  flex-wrap: wrap;
}

.smart-kind-row {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  margin-left: auto;
  flex-wrap: wrap;
}

.order-side-pill {
  display: inline-flex;
  align-items: center;
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 0.28rem 0.7rem;
  border-radius: 999px;
  border: 1px solid transparent;
}

.order-side-switch {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.order-side-switch .order-side-pill {
  cursor: pointer;
  background: transparent;
}

.order-side-switch .order-side-pill.is-idle {
  opacity: 0.42;
  filter: grayscale(0.35);
}

.order-side-switch .order-side-pill.is-active {
  opacity: 1;
  filter: none;
}

.order-side-switch .order-side-pill.is-long.is-active {
  border-color: rgba(5, 150, 105, 0.45);
}

.order-side-switch .order-side-pill.is-short.is-active {
  border-color: rgba(234, 88, 12, 0.45);
}

.order-side-pill.is-long {
  background: rgba(16, 185, 129, 0.14);
  color: #059669;
}

.order-side-pill.is-short {
  background: rgba(255, 138, 76, 0.16);
  color: #EA580C;
}

.order-side-pill.is-close {
  background: rgba(100, 116, 139, 0.14);
  color: #475569;
}

.order-card-dark .order-side-pill.is-long {
  background: rgba(16, 185, 129, 0.18);
  color: #34D399;
}

.order-card-dark .order-side-pill.is-short {
  color: #FF8A4C;
}

.order-card-dark .order-side-pill.is-close {
  background: rgba(148, 163, 184, 0.16);
  color: #CBD5E1;
}

.order-meta {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

.order-meta-card {
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 0.75rem;
  padding: 0.7rem 0.8rem;
}

.order-meta-quote {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
  align-items: start;
}

.order-meta-cell {
  min-width: 0;
}

.order-card-dark .order-meta-card {
  background: #1E293B;
  border-color: #334155;
}

.order-meta-label {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #94A3B8;
  margin-bottom: 0.2rem;
}

.order-meta-value {
  font-size: 1rem;
  font-weight: 800;
  color: #0F172A;
}

.order-card-dark .order-meta-value {
  color: #E2E8F0;
}

.order-meta-value.is-pos-long {
  color: #059669;
}

.order-meta-value.is-pos-short {
  color: #EA580C;
}

.order-card-dark .order-meta-value.is-pos-long {
  color: #34D399;
}

.order-card-dark .order-meta-value.is-pos-short {
  color: #FF8A4C;
}

.order-meta-change {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 0.45rem;
  font-size: 1rem;
}

.order-meta-change span:last-child {
  font-size: 0.82rem;
}

.order-meta-change .is-up {
  color: #059669;
}

.order-meta-change .is-down {
  color: #DC2626;
}

.order-meta-change .is-flat {
  color: #64748B;
}

.order-card-dark .order-meta-change .is-up {
  color: #34D399;
}

.order-card-dark .order-meta-change .is-down {
  color: #F87171;
}

.order-card-dark .order-meta-change .is-flat {
  color: #94A3B8;
}

.order-ways {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  width: 100%;
}

.manage-orders-hint {
  margin: 0;
  font-size: 0.78rem;
  line-height: 1.35;
  color: #64748B;
}

.order-card-dark .manage-orders-hint {
  color: #94A3B8;
}

.order-ways-top {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.65rem;
  width: 100%;
}

.order-ways-top.has-mm {
  grid-template-columns: 1fr 1fr;
}

.order-way-hint {
  margin-top: 0.15rem;
  font-size: 0.68rem;
  font-weight: 700;
  color: #94A3B8;
}

.has-mm .order-submit-max :deep(.btn) {
  min-width: 0;
  padding-left: 0.85rem;
  padding-right: 0.85rem;
}

.order-way {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.5rem;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  text-align: left;
  background: #F8FAFC;
  border: 1.5px solid #E2E8F0;
  border-radius: 0.85rem;
  padding: 0.75rem 0.8rem;
}

.order-card-dark .order-way {
  background: #1E293B;
  border-color: #334155;
}

.order-way-label {
  font-size: 0.65rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #94A3B8;
}

.order-way-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  width: 100%;
}

.order-way-value {
  font-size: 1.15rem;
  font-weight: 800;
  color: #0F172A;
  min-width: 0;
}

.order-card-dark .order-way-value {
  color: #E2E8F0;
}

.order-stepper {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: flex-start;
  gap: 0.3rem;
  width: 100%;
}

.order-stepper-with-action {
  display: flex !important;
  flex-direction: row !important;
  flex-wrap: nowrap !important;
  align-items: center;
}

.order-submit-inline-btn {
  flex: 0 0 auto;
  width: auto !important;
  min-width: 4.75rem;
  min-height: 34px;
  padding: 0.4rem 0.65rem !important;
  white-space: nowrap;
}

.order-stepper-with-action .order-submit-inline-btn {
  margin-left: auto;
}

.order-way-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
  width: 100%;
}

.order-way-head .order-way-hint {
  margin: 0;
  text-align: right;
}

.order-submit-max {
  flex: 0 0 auto;
  width: auto !important;
  margin: 0 !important;
}

.order-submit-max :deep(.btn) {
  min-width: 7.5rem;
  min-height: 36px;
  padding-left: 1.25rem;
  padding-right: 1.25rem;
}

.order-submit-full {
  width: 100% !important;
  margin: 0 !important;
}

.order-submit-full :deep(.btn) {
  width: 100%;
  min-height: 36px;
}

.order-stepper-btn {
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  border: 1.5px solid #CBD5E1;
  border-radius: 50%;
  background: #fff;
  color: #475569;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  font-size: 0.72rem;
  line-height: 1;
}

.order-stepper-btn:hover:not(:disabled) {
  color: #FF8A4C;
  border-color: #FF8A4C;
}

.order-stepper-btn:disabled {
  opacity: 0.35;
  cursor: default;
}

.order-stepper-btn-jump {
  font-size: 0.82rem;
  font-weight: 800;
}

.order-stepper-btn-jump :deep(svg) {
  font-size: 0.82rem;
}

.order-stepper-btn-preset {
  width: auto;
  min-width: 34px;
  padding: 0 0.35rem;
  border-radius: 999px;
  font-size: 0.58rem;
  font-weight: 800;
  letter-spacing: 0.04em;
}

.order-stepper-btn-preset.is-on {
  border-color: #FF8A4C;
  color: #EA580C;
  background: rgba(255, 138, 76, 0.12);
}

.order-card-dark .order-stepper-btn-preset.is-on {
  color: #FF8A4C;
  background: rgba(255, 138, 76, 0.16);
}

.jump-num {
  font-size: 0.52rem;
  font-weight: 800;
  letter-spacing: 0.01em;
  line-height: 1;
  margin-top: 0.02rem;
}

.order-card-dark .order-stepper-btn {
  background: #0F172A;
  border-color: #475569;
  color: #94A3B8;
}

.order-stepper-input {
  flex: 0 0 4.25rem;
  width: 4.25rem;
  min-width: 4.25rem;
  max-width: 4.25rem;
  height: 34px;
  border: 1.5px solid #E2E8F0;
  border-radius: 0.65rem;
  background: #fff;
  color: #0F172A;
  font-weight: 800;
  font-size: 0.95rem;
  text-align: center;
  outline: none;
  appearance: none;
  -moz-appearance: textfield;
}

.order-stepper-input:focus {
  border-color: #FF8A4C;
}

.order-card-dark .order-stepper-input {
  background: #0F172A;
  border-color: #475569;
  color: #E2E8F0;
}

.order-submit-long :deep(.btn),
.order-submit-long.btn {
  background: #10B981 !important;
  border-color: #10B981 !important;
}

.order-submit-short :deep(.btn),
.order-submit-short.btn {
  background: #FF8A4C !important;
  border-color: #FF8A4C !important;
}

.smart-order {
  width: 100%;
}

.smart-split {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.65rem;
  width: 100%;
}

.smart-split.is-pair {
  grid-template-columns: 1fr 1fr;
  gap: 0.85rem;
}

.smart-half {
  min-width: 0;
}

.smart-split.is-pair .smart-half + .smart-half {
  padding-left: 0.75rem;
  border-left: 1px solid #E2E8F0;
}

.order-card-dark .smart-split.is-pair .smart-half + .smart-half {
  border-left-color: #334155;
}

.smart-half .order-stepper-input {
  flex: 1 1 auto;
  width: auto;
  min-width: 0;
  max-width: none;
}

.smart-chip {
  border: 1px solid #E2E8F0;
  background: #fff;
  color: #64748B;
  border-radius: 999px;
  padding: 0.28rem 0.55rem;
  font-size: 0.65rem;
  font-weight: 800;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  cursor: pointer;
}

.order-card-dark .smart-chip {
  background: #0F172A;
  border-color: #475569;
  color: #94A3B8;
}

.smart-chip.is-active {
  border-color: #FF8A4C;
  color: #EA580C;
  background: rgba(255, 138, 76, 0.12);
}

.order-card-dark .smart-chip.is-active {
  color: #FF8A4C;
  background: rgba(255, 138, 76, 0.16);
}

.smart-kind-row .smart-chip.is-excluded {
  opacity: 0.38;
}

.order-way-hint.is-bad {
  color: #DC2626;
}

.order-card-dark .order-way-hint.is-bad {
  color: #F87171;
}

.smart-save-btn {
  width: 100%;
  border: 1.5px solid #FF8A4C;
  background: rgba(255, 138, 76, 0.1);
  color: #EA580C;
  font-weight: 800;
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  border-radius: 0.75rem;
  min-height: 36px;
}

.smart-save-btn:disabled {
  opacity: 0.4;
}

.smart-save-btn-dark {
  background: rgba(255, 138, 76, 0.14);
  color: #FF8A4C;
}
</style>
